import { useNavigate, useParams } from "react-router-dom";
import { GetProp, Table, TableProps } from "antd";
import { useEffect, useState } from "react";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import { useTranslation } from "react-i18next";
import { SubscriptionService } from "../../../../services/subscription.service";
import {
  IUserSubscriptionCancellationReason,
  IUserSubscriptionHistory,
} from "../../../../models/SubscriptionHistory";
import MainTitle from "../../../../components/ui/MainTitle/MainTitle";
import dayjs from "dayjs";
import { handleError } from "../../../../helper/errorHandler";
import dots from "../../../../assets/images/icons/dots.svg";
import viewIcon from "../../../../assets/images/icons/eye-dark.svg";
import CancellationReasonPopup from "./CancellationReasonPopup/CancellationReasonPopup";

type ColumnsType<T> = TableProps<T>["columns"];
type TablePaginationConfig = Exclude<GetProp<TableProps, "pagination">, boolean>;

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Parameters<GetProp<TableProps, "onChange">>[1];
}

const ClientSubscriptionsHistory = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { id } = useParams();
  const [data, setData] = useState<IUserSubscriptionHistory[]>();
  const [loading, setLoading] = useState(false);
  const [selectedForCheckReason, setSelectedForCheckReason] =
    useState<IUserSubscriptionCancellationReason | null>(null);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  useEffect(() => {
    getSubscriptionHistory(tableParams.pagination?.current, tableParams.pagination?.pageSize);
  }, [tableParams.pagination?.current, tableParams.pagination?.pageSize]);

  if (!id) {
    navigate("/clients");
    return null;
  }

  const columns: ColumnsType<IUserSubscriptionHistory> = [
    {
      title: t("FirstName"),
      render: (data: IUserSubscriptionHistory) => {
        return data.user.firstname;
      },
    },
    {
      title: t("LastName"),
      render: (data: IUserSubscriptionHistory) => {
        return data.user.lastname;
      },
    },
    {
      title: t("Email"),
      render: (data: IUserSubscriptionHistory) => {
        return data.user.email;
      },
    },
    {
      title: t("Subscription"),
      render: (data: IUserSubscriptionHistory) => {
        return (
          <>
            {data.userSubscription.subscription.credits} {t("Credits")}
          </>
        );
      },
    },
    {
      title: t("StartDate"),
      render: (data: IUserSubscriptionHistory) => {
        return dayjs(data.startDate).format("DD/MM/YYYY");
      },
    },
    {
      title: t("ExpiryDate"),
      render: (data: IUserSubscriptionHistory) => {
        return dayjs(data.endDate).format("DD/MM/YYYY");
      },
    },
    {
      title: t("CancellationReason"),
      render: (data: IUserSubscriptionHistory) => {
        if (data.userSubscriptionCancellationReason) {
          return data.userSubscriptionCancellationReason.reason;
        }
        return "-";
      },
    },
    {
      title: t("Action"),
      render: (data: IUserSubscriptionHistory) => {
        return (
          <div className={"action-wrapper"}>
            <img src={dots} alt={"action"} />
            <ul className={"dropdown"}>
              <li
                className={"dropdown-item"}
                onClick={() => setSelectedForCheckReason(data.userSubscriptionCancellationReason)}
              >
                <img src={viewIcon} alt={"Preview"} />
                {t("CheckReason")}
              </li>
            </ul>
          </div>
        );
      },
    },
  ];

  const handleTableChange: TableProps<IUserSubscriptionHistory>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  const getSubscriptionHistory = async (
    currentPage: number | undefined,
    pageSize: number | undefined
  ) => {
    try {
      setLoading(true);
      const response = await SubscriptionService.getClientSubscriptionHistoryWithPagination(
        id,
        currentPage,
        pageSize
      );
      if (response.status === 200) {
        setLoading(false);
        setData(response.data.items);
        setTableParams({
          ...tableParams,
          pagination: {
            ...tableParams.pagination,
            total: response.data.total,
          },
        });
      }
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  return (
    <div className={"gyms-page"}>
      <MainTitle text={t("SubscriptionHistory")} />
      <Table
        className={"table"}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={data}
        pagination={tableParams.pagination}
        loading={loading}
        onChange={handleTableChange}
      />
      {selectedForCheckReason && (
        <CancellationReasonPopup
          data={selectedForCheckReason}
          closePopup={() => setSelectedForCheckReason(null)}
        />
      )}
    </div>
  );
};

export default ClientSubscriptionsHistory;
